import axios from 'axios'
import type { ApiResponse } from '@/types'
import type { AxiosRequestConfig } from 'axios'
import { getMessage } from './messageHolder'
import { setCryptoConfig, isEncryptEnabled, aesEncrypt, aesDecrypt } from './crypto'

const _k = ['NjgwOQ==', 'OGNhZQ==', 'MmFmYw==', 'NTg5Mg==']
const _key = atob(_k[3]) + atob(_k[1]) + atob(_k[0]) + atob(_k[2])
const _iv = String.fromCharCode(101, 57, 100, 55, 51, 50, 97, 49, 101, 100, 99, 100, 99, 99, 48, 97)
setCryptoConfig({ enable: true, key: _key, iv: _iv })

const ENCRYPT_METHODS = new Set(['post', 'put', 'delete', 'patch'])

const request = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
})

// 请求拦截: 注入 Token + Language，并对 JSON body 加密
request.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const lang = localStorage.getItem('lang') || 'zh-CN'
  config.headers['Accept-Language'] = lang

  // 注入 TOTP 临时令牌（用于 TRON 敏感操作验证）
  const totpToken = sessionStorage.getItem('tron_totp_token')
  const totpExpiry = parseInt(sessionStorage.getItem('tron_totp_token_expiry') || '0', 10)
  if (totpToken && Date.now() < totpExpiry && !config.headers['X-TOTP-Token']) {
    config.headers['X-TOTP-Token'] = totpToken
  }

  if (
    isEncryptEnabled() &&
    ENCRYPT_METHODS.has((config.method || '').toLowerCase()) &&
    config.data != null &&
    !(config.data instanceof FormData)
  ) {
    const plaintext = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)
    const ciphertext = await aesEncrypt(plaintext)
    config.data = { data: ciphertext }
  }
  return config
})

// 响应拦截：解密 data 字段（加密启用时后端返回 Base64 密文字符串）
request.interceptors.response.use(
  async (res) => {
    const data = res.data as ApiResponse
    if (data.code !== 0) {
      getMessage().error(data.message || '请求失败')
      if (data.code === 401 || res.status === 401) {
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
      // 10011 = response.ErrTOTPUnbound，专用于"该操作要求管理员本人已绑定
      // TOTP，当前未绑定"。不能用更早的 10006（response.ErrPermission）——
      // 那个码后端多处复用为通用"无权限"错误，全局按它跳转会误伤无关功能。
      if (data.code === 10011) {
        window.location.href = '/totp-security'
      }
      return Promise.reject(Object.assign(new Error(data.message), { code: data.code, trc20ReviewOutcome: res.headers['x-trc20-review-outcome'], bankRequestOutcome: res.headers['x-bank-request-outcome'], bankRequestKey: res.headers['x-bank-request-key'] }))
    }
    if (isEncryptEnabled() && typeof data.data === 'string' && data.data !== '') {
      try {
        const decrypted = await aesDecrypt(data.data)
        data.data = JSON.parse(decrypted)
      } catch {
        // 解密失败说明该响应本身就是字符串（如 OKMsg 场景），保持原值
      }
    }
    return res
  },
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    getMessage().error(err.response?.data?.message || '网络错误')
    return Promise.reject(err)
  }
)

// 封装方法
export const http = {
  get: <T = any>(url: string, params?: any) =>
    request.get<ApiResponse<T>>(url, { params }).then(r => r.data.data),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    request.post<ApiResponse<T>>(url, data, config).then(r => r.data.data),

  put: <T = any>(url: string, data?: any) =>
    request.put<ApiResponse<T>>(url, data).then(r => r.data.data),

  delete: <T = any>(url: string) =>
    request.delete<ApiResponse<T>>(url).then(r => r.data.data),

  /** 文件上传（multipart/form-data） */
  upload: <T = { url: string; name: string; size: number; type: string }>(
    file: File,
    dir?: string
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (dir) form.append('dir', dir)
    return request
      .post<ApiResponse<T>>('/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data.data)
  },
}

export default request
